import * as angular from "angular"
import { ClientActivity, defineComponent, FieldViewModel, IGenericAnalysis, ILaboratory, IOmniaShell, isUndefinedOrNull, OmniaObjectType, shell } from "../omnia"
import { IAnalysisEditCommand, ISoilService } from "../types"

export abstract class AnalysisEditCtrl<T extends IGenericAnalysis> extends shell.Dialog {

    protected static create(controllerType: Function): ng.IDirective {

        return defineComponent(controllerType, "app/soil/edit/popup.html")

    }

    protected abstract getTypeInfo(): { title?: string, name?: string, article?: string, type: OmniaObjectType }
    protected abstract getPopupTemplate(): string
    protected abstract setupAnalysis(a: IGenericAnalysis)
    protected abstract saveAnalysis(): ng.IPromise<number>
    protected abstract deleteAnalysis(deleteOtherGridSamples: boolean): ng.IPromise<any>
    protected abstract getActivity(): ClientActivity

    hideDateInId: boolean
    hideLaboratory: boolean
    showFieldRefInId: boolean

    protected cmd: IAnalysisEditCommand

    public newAnalysis: boolean

    public analysis: T

    protected laboratories: ILaboratory[]
    protected laboratory: ILaboratory

    protected allocTargets: FieldViewModel[]
    protected _allocTarget: FieldViewModel
    protected selectedAllocTargets: FieldViewModel[]
    protected deletedAllocTargets: FieldViewModel[]
    protected currentAllocSelection: FieldViewModel

    static $inject = ["$scope", "OmniaSoilService", "OmniaShell"]

    constructor(
        $scope: ng.IScope,
        protected svc: ISoilService,
        shell: IOmniaShell,
        private readonly readOnly = shell.isReadOnly(p => p.soil)) {

        super($scope, shell)

    }

    protected setup() {

        this.updateTitle()

        this.laboratories = this.svc.getLaboratories()

        this.setupFromArguments()

    }

    protected setupFromArguments() {
        let cmd = <IAnalysisEditCommand>this.arguments
        this.setupEditor(cmd)
    }

    protected setupEditor(cmd: IAnalysisEditCommand) {

        this.cmd = cmd

        this.newAnalysis = false
        this.analysis = null

        if (!cmd) {
            return
        }

        let analysis = cmd.analysis as T

        if (analysis && cmd.edit) {

            analysis = angular.merge({}, analysis)

        }
        else {

            this.newAnalysis = true

            let samplingDate = new Date()

            analysis = <any>{
                SamplingDate: samplingDate,
                FirstYear: this.svc.yearId
            }

        }

        this.laboratory = null

        if (analysis) {
            this.laboratory = this.laboratories.find(l => l.LaboratoryId == analysis.LaboratoryId)
        }

        this.analysis = analysis

        this.setupAnalysis(analysis)

        this.updateTitle()

        this.setActivity(this.getActivity())

    }

    private updateTitle() {

        this._icon = this.newAnalysis ? "icon-addnew" : "fas fa-pen"

        const { title, article } = this.getTypeInfo()
        this._title = (this.newAnalysis ? "Create" : "Update") + ` ${article || "a"} ` + title

    }

    getIconCssClass() {
        return `icon-${this.newAnalysis ? 'addnew' : 'fas fa-pen'}`
    }

    getIconCssClassShort() {
        return this.newAnalysis ? 'addnew' : 'fas fa-pen'
    }

    protected isCroppingScoped() {
        return false
    }

    get allocTarget(): FieldViewModel {
        return this._allocTarget
    }

    set allocTarget(value: FieldViewModel) {
        if (this._allocTarget != value) {
            this._allocTarget = value
            this.updateOnAllocTargetChanged(value)
        }
    }

    protected updateOnAllocTargetChanged(target: FieldViewModel) {
        // NOP
    }

    get samplingDate(): Date {
        if (this.analysis) {
            return this.analysis.SamplingDate
        }
    }

    set samplingDate(value: Date) {
        this.analysis.SamplingDate = value
        this.updateOnDateChanged()
    }

    protected updateOnDateChanged() {
        // NOP
    }


    protected validate() {
        return true
    }

    selectField(field: FieldViewModel) {
        if (field) {
            this.currentAllocSelection = null

            if (this.selectedAllocTargets.indexOf(field) === -1 && this.deletedAllocTargets.indexOf(field) === -1) {
                this.selectedAllocTargets.push(field)
            }
        }
    }

    unselectField(field: FieldViewModel) {
        if (field && this.selectedAllocTargets.indexOf(field) !== -1) {

            if (this.analysis.Allocations && this.analysis.Allocations.filter(a => a.FieldId === field.fieldId).length > 0) {
                if (this.deletedAllocTargets.indexOf(field) === -1) {
                    this.deletedAllocTargets.push(field)
                }
            }

            this.selectedAllocTargets = this.selectedAllocTargets.filter(f => field !== f)
        }
    }

    recoverField(field: FieldViewModel) {
        if (field && this.deletedAllocTargets.indexOf(field) !== -1) {
            this.deletedAllocTargets = this.deletedAllocTargets.filter(f => field !== f)

            if (this.selectedAllocTargets.indexOf(field) === -1) {
                this.selectedAllocTargets.push(field)
            }
        }
    }

    canCommit(): boolean {

        if (this.readOnly) return false
        if (this.form && !this.form.$valid) return false
        if (!this.validate()) return false

        return true

    }

    private refreshAndClose(id: number) {

        if (this.cmd && this.cmd.context) {
            this.cmd.context.refresh(id)
        }

        this.closeComponent(id)

    }

    commit(): void {

        if (!this.canCommit()) {
            this.showValidationErrors = true
            return
        }

        this.showValidationErrors = false

        let session = this.shell.getSession()
        this.analysis.HoldingId = session.holdingId

        this.analysis.LaboratoryId = this.laboratory ? this.laboratory.LaboratoryId : null

        let result = this.saveAnalysis()

        if (result) {
            result.then(r => this.refreshAndClose(r))
        }

    }

    canDelete() {
        return (!this.newAnalysis) && (!!this.analysis) && (!this.readOnly)
    }

    delete() {

        const typeName = this.getTypeInfo().title

        var content = "<p>Click 'Delete' to delete this analysis, and all its current allocations.</p><p>Please note that this operation is irreversible.</p>"

        var p = shell.showDeleteCancel(this.shell, content, `Delete this ${typeName}`)

        p.then(r => {

            if (r) {

                return this.promptDeleteGridSample()
                    .then(r => {

                        if (!isUndefinedOrNull(r)) {

                            this.deleteAnalysis(r)
                                .then(() => this.refreshAndClose(-1))

                        }

                    })

            }

        })

    }


    private promptDeleteGridSample() {

        let prompt = this.shell.resolve(false)

        if (this.checkSingleAllocGridSample()) {

            let content = "<p>This analysis was imported from a grid samples file.</p><p>Do you want to delete the associated map layer, and all the other analysis that were imported along this one?</p>"

            prompt = this.shell.showMessageBox({

                content: content,
                title: "Delete associated grid samples",

                buttons: [
                    { text: "Cancel", value: null, cssClass: "btn-warning pull-left" },
                    { text: "<i class='icon icon-delete'></i>&nbsp;Yes, delete the layer and analysis", value: true, cssClass: "btn-danger" },
                    { text: "No, keep everything else", value: false, cssClass: "btn-primary" }
                ]

            })

        }

        return prompt

    }

    private checkSingleAllocGridSample() {

        if (!this.analysis) return false
        if (!this.analysis.GridSample) return false
        if (!this.analysis.Allocations) return false
        if (this.analysis.Allocations.length != 1) return false
        if (!this.analysis.Allocations[0].ZoneId) return false

        return true

    }


}
